<div class="manual-docs">
  <!-- Toolbar with search -->
  <div class="manual-toolbar">
    <div class="manual-search">
      <i class="pi pi-search manual-search-icon"></i>
      <input
        type="text"
        class="manual-search-input"
        [(ngModel)]="searchQuery"
        (ngModelChange)="onSearch()"
        [placeholder]="'USER_MANUAL.SEARCH_PLACEHOLDER' | translate"
        autocomplete="off"
      />
      @if (searchQuery) {
        <button class="manual-search-clear" (click)="clearSearch()"><i class="pi pi-times"></i></button>
      }
    </div>
    <button class="print-btn" (click)="savePdf()">
      <i class="pi pi-download"></i>
      {{ 'USER_MANUAL.SAVE_AS_PDF' | translate }}
    </button>
  </div>

  <!-- Sidebar navigation -->
  <aside class="manual-sidebar">
    <p class="sidebar-title">{{ 'USER_MANUAL.CONTENTS' | translate }}</p>
    <nav>
      <ul class="sidebar-nav">
        @for (section of topLevelSections; track section.id) {
          <li class="sidebar-item">
            <a (click)="scrollTo(section.id)" class="sidebar-link" [class.active]="activeSection === section.id" (keydown.enter)="scrollTo(section.id)" tabindex="0" role="button">
              {{ section.labelKey | translate }}
            </a>
            @if (getChildren(section.id).length > 0) {
              <ul class="sidebar-children">
                @for (child of getChildren(section.id); track child.id) {
                  <li>
                    <a
                      (click)="scrollTo(child.id)"
                      class="sidebar-link sidebar-link--sub"
                      [class.active]="activeSection === child.id"
                      (keydown.enter)="scrollTo(child.id)"
                      tabindex="0"
                      role="button"
                    >
                      {{ child.labelKey | translate }}
                    </a>
                  </li>
                }
              </ul>
            }
          </li>
        }
        @if (searchQuery && topLevelSections.length === 0) {
          <li class="sidebar-no-results">{{ 'USER_MANUAL.NO_RESULTS' | translate }}</li>
        }
      </ul>
    </nav>
  </aside>

  <!-- Main content -->
  <main class="manual-main">
    <section id="overview" class="manual-section">
      <h1 class="manual-page-title">{{ 'USER_MANUAL.NAV.OVERVIEW' | translate }}</h1>
      <div [innerHTML]="c('overview-body') | safeHtml"></div>
      <div class="screen-placeholder">
        <div class="screen-mock">
          <div class="screen-mock-topbar"><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span></div>
          <div class="screen-mock-body">
            <div class="screen-mock-sidebar"></div>
            <div class="screen-mock-content">
              <div class="screen-mock-kpi-row">
                <div class="screen-mock-kpi"></div>
                <div class="screen-mock-kpi"></div>
                <div class="screen-mock-kpi"></div>
                <div class="screen-mock-kpi"></div>
              </div>
              <div class="screen-mock-chart"></div>
            </div>
          </div>
        </div>
        <p class="screen-caption">{{ 'USER_MANUAL.SCREENSHOT' | translate }}: Portal Dashboard Overview</p>
      </div>
    </section>

    <hr class="manual-divider" />

    <section id="getting-started" class="manual-section">
      <h1 class="manual-page-title">{{ 'USER_MANUAL.NAV.GETTING_STARTED' | translate }}</h1>
      <div [innerHTML]="c('gs-intro') | safeHtml"></div>
    </section>

    <section id="gs-login" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.GS_LOGIN' | translate }}</h2>
      <div [innerHTML]="c('gs-login-body') | safeHtml"></div>
      <div class="screen-placeholder">
        <div class="screen-mock screen-mock--narrow">
          <div class="screen-mock-topbar"><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span></div>
          <div class="screen-mock-body screen-mock-body--center">
            <div class="screen-mock-form">
              <div class="screen-mock-field"></div>
              <div class="screen-mock-field"></div>
              <div class="screen-mock-button"></div>
            </div>
          </div>
        </div>
        <p class="screen-caption">{{ 'USER_MANUAL.SCREENSHOT' | translate }}: Login Screen</p>
      </div>
    </section>

    <section id="gs-navigation" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.GS_NAVIGATION' | translate }}</h2>
      <div [innerHTML]="c('gs-navigation-body') | safeHtml"></div>
    </section>

    <section id="gs-language" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.GS_LANGUAGE' | translate }}</h2>
      <div [innerHTML]="c('gs-language-body') | safeHtml"></div>
    </section>

    <hr class="manual-divider" />

    <section id="access-control" class="manual-section">
      <h1 class="manual-page-title">{{ 'USER_MANUAL.NAV.ACCESS_CONTROL' | translate }}</h1>
      <div [innerHTML]="c('access-control-body') | safeHtml"></div>
    </section>

    <section id="ac-dashboard" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.DASHBOARD' | translate }}</h2>
      <div [innerHTML]="c('ac-dashboard-body') | safeHtml"></div>
      <div class="screen-placeholder">
        <div class="screen-mock">
          <div class="screen-mock-topbar"><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span></div>
          <div class="screen-mock-body">
            <div class="screen-mock-sidebar"></div>
            <div class="screen-mock-content">
              <div class="screen-mock-kpi-row">
                <div class="screen-mock-kpi"></div>
                <div class="screen-mock-kpi"></div>
                <div class="screen-mock-kpi"></div>
                <div class="screen-mock-kpi"></div>
              </div>
              <div class="screen-mock-grid-cards">
                <div class="screen-mock-card"></div>
                <div class="screen-mock-card"></div>
                <div class="screen-mock-card"></div>
                <div class="screen-mock-card"></div>
                <div class="screen-mock-card"></div>
                <div class="screen-mock-card"></div>
              </div>
            </div>
          </div>
        </div>
        <p class="screen-caption">{{ 'USER_MANUAL.SCREENSHOT' | translate }}: Access Control Dashboard</p>
      </div>
    </section>

    <section id="ac-passes" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.AC_PASSES' | translate }}</h2>
      <div [innerHTML]="c('ac-passes-body') | safeHtml"></div>
      <div class="screen-placeholder">
        <div class="screen-mock">
          <div class="screen-mock-topbar"><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span></div>
          <div class="screen-mock-body">
            <div class="screen-mock-sidebar"></div>
            <div class="screen-mock-content">
              <div class="screen-mock-table-header"></div>
              <div class="screen-mock-table-row"></div>
              <div class="screen-mock-table-row"></div>
              <div class="screen-mock-table-row"></div>
              <div class="screen-mock-table-row"></div>
              <div class="screen-mock-table-row"></div>
            </div>
          </div>
        </div>
        <p class="screen-caption">{{ 'USER_MANUAL.SCREENSHOT' | translate }}: Access Passes List</p>
      </div>
    </section>

    <section id="ac-vehicles" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.VEHICLES' | translate }}</h2>
      <div [innerHTML]="c('ac-vehicles-body') | safeHtml"></div>
    </section>

    <section id="ac-hme" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.HME' | translate }}</h2>
      <div [innerHTML]="c('ac-hme-body') | safeHtml"></div>
    </section>

    <section id="ac-employees" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.EMPLOYEES' | translate }}</h2>
      <div [innerHTML]="c('ac-employees-body') | safeHtml"></div>
      <div class="screen-placeholder">
        <div class="screen-mock">
          <div class="screen-mock-topbar"><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span></div>
          <div class="screen-mock-body">
            <div class="screen-mock-sidebar"></div>
            <div class="screen-mock-content">
              <div class="screen-mock-table-header"></div>
              <div class="screen-mock-table-row"></div>
              <div class="screen-mock-table-row"></div>
              <div class="screen-mock-table-row"></div>
            </div>
          </div>
        </div>
        <p class="screen-caption">{{ 'USER_MANUAL.SCREENSHOT' | translate }}: Employee Management</p>
      </div>
    </section>

    <section id="ac-messages" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.MESSAGES' | translate }}</h2>
      <div [innerHTML]="c('ac-messages-body') | safeHtml"></div>
    </section>

    <section id="ac-reports" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.REPORTS' | translate }}</h2>
      <div [innerHTML]="c('ac-reports-body') | safeHtml"></div>
    </section>

    <hr class="manual-divider" />

    <section id="culture" class="manual-section">
      <h1 class="manual-page-title">{{ 'USER_MANUAL.NAV.CULTURE' | translate }}</h1>
      <div [innerHTML]="c('culture-body') | safeHtml"></div>
    </section>

    <section id="cu-dashboard" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.DASHBOARD' | translate }}</h2>
      <div [innerHTML]="c('cu-dashboard-body') | safeHtml"></div>
    </section>

    <section id="cu-events" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.EVENTS' | translate }}</h2>
      <div [innerHTML]="c('cu-events-body') | safeHtml"></div>
      <div class="screen-placeholder">
        <div class="screen-mock">
          <div class="screen-mock-topbar"><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span></div>
          <div class="screen-mock-body">
            <div class="screen-mock-sidebar"></div>
            <div class="screen-mock-content">
              <div class="screen-mock-table-header"></div>
              <div class="screen-mock-table-row screen-mock-table-row--highlight"></div>
              <div class="screen-mock-table-row"></div>
              <div class="screen-mock-table-row"></div>
              <div class="screen-mock-table-row"></div>
            </div>
          </div>
        </div>
        <p class="screen-caption">{{ 'USER_MANUAL.SCREENSHOT' | translate }}: Events List</p>
      </div>
    </section>

    <section id="cu-subscribers" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.SUBSCRIBERS' | translate }}</h2>
      <div [innerHTML]="c('cu-subscribers-body') | safeHtml"></div>
    </section>

    <section id="cu-messages" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.MESSAGES' | translate }}</h2>
      <div [innerHTML]="c('cu-messages-body') | safeHtml"></div>
    </section>

    <hr class="manual-divider" />

    <section id="courses" class="manual-section">
      <h1 class="manual-page-title">{{ 'USER_MANUAL.NAV.COURSES' | translate }}</h1>
      <div [innerHTML]="c('courses-body') | safeHtml"></div>
    </section>

    <section id="co-dashboard" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.DASHBOARD' | translate }}</h2>
      <div [innerHTML]="c('co-dashboard-body') | safeHtml"></div>
    </section>

    <section id="co-events" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.EVENTS' | translate }}</h2>
      <div [innerHTML]="c('co-events-body') | safeHtml"></div>
    </section>

    <section id="co-students" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.CO_STUDENTS' | translate }}</h2>
      <div [innerHTML]="c('co-students-body') | safeHtml"></div>
    </section>

    <section id="co-reports" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.REPORTS' | translate }}</h2>
      <div [innerHTML]="c('co-reports-body') | safeHtml"></div>
    </section>

    <hr class="manual-divider" />

    <section id="residential" class="manual-section">
      <h1 class="manual-page-title">{{ 'USER_MANUAL.NAV.RESIDENTIAL' | translate }}</h1>
      <div [innerHTML]="c('residential-body') | safeHtml"></div>
    </section>

    <section id="re-dashboard" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.DASHBOARD' | translate }}</h2>
      <div [innerHTML]="c('re-dashboard-body') | safeHtml"></div>
    </section>

    <section id="re-passes" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.AC_PASSES' | translate }}</h2>
      <div [innerHTML]="c('re-passes-body') | safeHtml"></div>
    </section>

    <section id="re-apartments" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.RE_APARTMENTS' | translate }}</h2>
      <div [innerHTML]="c('re-apartments-body') | safeHtml"></div>
    </section>

    <section id="re-messages" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.MESSAGES' | translate }}</h2>
      <div [innerHTML]="c('re-messages-body') | safeHtml"></div>
    </section>

    <hr class="manual-divider" />

    <section id="kindergarten" class="manual-section">
      <h1 class="manual-page-title">{{ 'USER_MANUAL.NAV.KINDERGARTEN' | translate }}</h1>
      <div [innerHTML]="c('kindergarten-body') | safeHtml"></div>
    </section>

    <section id="ki-dashboard" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.DASHBOARD' | translate }}</h2>
      <div [innerHTML]="c('ki-dashboard-body') | safeHtml"></div>
    </section>

    <section id="ki-passes" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.AC_PASSES' | translate }}</h2>
      <div [innerHTML]="c('ki-passes-body') | safeHtml"></div>
    </section>

    <section id="ki-children" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.KI_CHILDREN' | translate }}</h2>
      <div [innerHTML]="c('ki-children-body') | safeHtml"></div>
    </section>

    <section id="ki-employees" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.EMPLOYEES' | translate }}</h2>
      <div [innerHTML]="c('ki-employees-body') | safeHtml"></div>
    </section>

    <section id="ki-messages" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.MESSAGES' | translate }}</h2>
      <div [innerHTML]="c('ki-messages-body') | safeHtml"></div>
    </section>

    <section id="ki-reports" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.REPORTS' | translate }}</h2>
      <div [innerHTML]="c('ki-reports-body') | safeHtml"></div>
    </section>

    <hr class="manual-divider" />

    <section id="nursing" class="manual-section">
      <h1 class="manual-page-title">{{ 'USER_MANUAL.NAV.NURSING' | translate }}</h1>
      <div [innerHTML]="c('nursing-body') | safeHtml"></div>
    </section>

    <section id="nu-dashboard" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.DASHBOARD' | translate }}</h2>
      <div [innerHTML]="c('nu-dashboard-body') | safeHtml"></div>
    </section>

    <section id="nu-orders" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.NU_ORDERS' | translate }}</h2>
      <div [innerHTML]="c('nu-orders-body') | safeHtml"></div>
    </section>

    <section id="nu-messages" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.MESSAGES' | translate }}</h2>
      <div [innerHTML]="c('nu-messages-body') | safeHtml"></div>
    </section>

    <section id="nu-reports" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.REPORTS' | translate }}</h2>
      <div [innerHTML]="c('nu-reports-body') | safeHtml"></div>
    </section>

    <hr class="manual-divider" />

    <section id="timewatch" class="manual-section">
      <h1 class="manual-page-title">{{ 'USER_MANUAL.NAV.TIMEWATCH' | translate }}</h1>
      <div [innerHTML]="c('timewatch-body') | safeHtml"></div>
    </section>

    <section id="tw-dashboard" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.DASHBOARD' | translate }}</h2>
      <div [innerHTML]="c('tw-dashboard-body') | safeHtml"></div>
      <div class="screen-placeholder">
        <div class="screen-mock">
          <div class="screen-mock-topbar"><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span></div>
          <div class="screen-mock-body">
            <div class="screen-mock-sidebar"></div>
            <div class="screen-mock-content">
              <div class="screen-mock-kpi-row">
                <div class="screen-mock-kpi"></div>
                <div class="screen-mock-kpi"></div>
                <div class="screen-mock-kpi"></div>
                <div class="screen-mock-kpi"></div>
              </div>
              <div class="screen-mock-table-header"></div>
              <div class="screen-mock-table-row"></div>
              <div class="screen-mock-table-row"></div>
              <div class="screen-mock-table-row"></div>
            </div>
          </div>
        </div>
        <p class="screen-caption">{{ 'USER_MANUAL.SCREENSHOT' | translate }}: Time Watch Dashboard</p>
      </div>
    </section>

    <section id="tw-employees" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.EMPLOYEES' | translate }}</h2>
      <div [innerHTML]="c('tw-employees-body') | safeHtml"></div>
    </section>

    <section id="tw-messages" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.MESSAGES' | translate }}</h2>
      <div [innerHTML]="c('tw-messages-body') | safeHtml"></div>
    </section>

    <section id="tw-reports" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.REPORTS' | translate }}</h2>
      <div [innerHTML]="c('tw-reports-body') | safeHtml"></div>
    </section>

    <hr class="manual-divider" />

    <section id="entry-monitor" class="manual-section">
      <h1 class="manual-page-title">{{ 'USER_MANUAL.NAV.ENTRY_MONITOR' | translate }}</h1>
      <div [innerHTML]="c('entry-monitor-body') | safeHtml"></div>
      <div class="screen-placeholder">
        <div class="screen-mock screen-mock--dark">
          <div class="screen-mock-topbar screen-mock-topbar--dark"><span class="screen-mock-dot"></span><span class="screen-mock-dot"></span></div>
          <div class="screen-mock-body screen-mock-body--dark">
            <div class="screen-mock-monitor-row screen-mock-monitor-row--green"></div>
            <div class="screen-mock-monitor-row"></div>
            <div class="screen-mock-monitor-row screen-mock-monitor-row--red"></div>
            <div class="screen-mock-monitor-row"></div>
          </div>
        </div>
        <p class="screen-caption">{{ 'USER_MANUAL.SCREENSHOT' | translate }}: Entry Monitor — Live Feed</p>
      </div>
    </section>

    <hr class="manual-divider" />

    <section id="settings" class="manual-section">
      <h1 class="manual-page-title">{{ 'USER_MANUAL.NAV.SETTINGS' | translate }}</h1>
      <div [innerHTML]="c('settings-body') | safeHtml"></div>
    </section>

    <section id="se-business" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.SE_BUSINESS' | translate }}</h2>
      <div [innerHTML]="c('se-business-body') | safeHtml"></div>
    </section>

    <section id="se-users" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.SE_USERS' | translate }}</h2>
      <div [innerHTML]="c('se-users-body') | safeHtml"></div>
    </section>

    <section id="se-billing" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.SE_BILLING' | translate }}</h2>
      <div [innerHTML]="c('se-billing-body') | safeHtml"></div>
    </section>

    <section id="se-locations" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.SE_LOCATIONS' | translate }}</h2>
      <div [innerHTML]="c('se-locations-body') | safeHtml"></div>
    </section>

    <section id="se-clubs" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.SE_CLUBS' | translate }}</h2>
      <div [innerHTML]="c('se-clubs-body') | safeHtml"></div>
    </section>

    <section id="se-permissions" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.SE_PERMISSIONS' | translate }}</h2>
      <div [innerHTML]="c('se-permissions-body') | safeHtml"></div>
    </section>

    <section id="se-requirements" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.SE_REQUIREMENTS' | translate }}</h2>
      <div [innerHTML]="c('se-requirements-body') | safeHtml"></div>
    </section>

    <section id="se-uploads" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.SE_UPLOADS' | translate }}</h2>
      <div [innerHTML]="c('se-uploads-body') | safeHtml"></div>
    </section>

    <section id="se-access" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.SE_ACCESS' | translate }}</h2>
      <div [innerHTML]="c('se-access-body') | safeHtml"></div>
    </section>

    <section id="se-station" class="manual-section">
      <h2 class="manual-section-title">{{ 'USER_MANUAL.NAV.SE_STATION' | translate }}</h2>
      <div [innerHTML]="c('se-station-body') | safeHtml"></div>
    </section>

    <div class="manual-bottom-spacer"></div>
  </main>
</div>
